import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { CITIES, hubSlug, BELO_PROVIDER } from "@/data/cityLocations";
import { MapPin, Plane, ChevronRight, ArrowRight } from "lucide-react";

const SITE = "https://belorentals.com";

const GROUPS = [
  {
    title: "Boston & the inner core",
    blurb: "Dense, transit-rich neighborhoods where a rental shines for day trips, errands and getaways beyond the T.",
    cities: ["boston", "cambridge", "somerville", "brookline", "watertown"],
  },
  {
    title: "Malden & northern neighbors",
    blurb: "Our home base and the Orange Line communities just north of the city — quick pickups close to I-93 and Route 1.",
    cities: ["malden", "everett", "medford", "melrose", "chelsea"],
  },
  {
    title: "North Shore & the coast",
    blurb: "Blue Line beach towns and North Shore routes, minutes from Logan and the drive up Route 1.",
    cities: ["revere", "lynn", "saugus"],
  },
  {
    title: "Northwest suburbs",
    blurb: "Route 128 / I-95 tech-belt towns where most trips simply need a car.",
    cities: ["woburn", "burlington", "lexington"],
  },
];

export default function LocationsDirectory() {
  const path = "/car-rental-locations";
  const breadcrumb = [
    { name: "Home", to: "/" },
    { name: "Locations We Serve", to: path },
  ];
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumb.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name, item: `${SITE}${b.to}` })),
  };
  const allLinks = [
    { name: "Logan Airport Car Rental", url: `${SITE}/logan-airport-car-rental` },
    ...GROUPS.flatMap((g) => g.cities.filter((c) => CITIES[c]).map((c) => ({ name: `Car Rental in ${CITIES[c].cityName}, MA`, url: `${SITE}/${hubSlug(c)}` }))),
  ];
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Belo Rentals — Car Rental Locations Across Greater Boston",
    url: `${SITE}${path}`,
    about: { "@type": "AutoRental", ...BELO_PROVIDER },
    mainEntity: { "@type": "ItemList", itemListElement: allLinks.map((l, i) => ({ "@type": "ListItem", position: i + 1, name: l.name, url: l.url })) },
  };

  return (
    <div data-testid="locations-directory" className="bg-white">
      <Seo
        title="Car Rental Locations Across Greater Boston | Belo Rentals"
        description="Belo Rentals serves Boston, Cambridge, Malden, Revere, Logan Airport and 15+ Greater Boston communities. Explore our full service area and pick up a clean, well-maintained car from $50/day."
        path={path}
        keywords="car rental locations Boston, Greater Boston car rental, car rental near me Massachusetts, Belo Rentals service area"
        jsonLd={[breadcrumbLd, collectionLd]}
      />

      <section className="pt-28 pb-12 px-5 sm:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="Breadcrumb" data-testid="breadcrumb">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500">
              {breadcrumb.map((b, i) => (
                <li key={b.to} className="flex items-center gap-1.5">
                  {i < breadcrumb.length - 1 ? <Link to={b.to} className="hover:text-blue-600">{b.name}</Link> : <span className="text-slate-800 font-medium" aria-current="page">{b.name}</span>}
                  {i < breadcrumb.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
                </li>
              ))}
            </ol>
          </nav>
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-medium mt-6"><MapPin size={15} /> Greater Boston service area</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-4">Car rental locations across Greater Boston</h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Belo Rentals is a Massachusetts car rental marketplace with its main office in Leominster and pickup points in Malden and Clinton — serving riders throughout the Boston metro, from the airport and the inner core out to the North Shore and the Route&nbsp;128 suburbs. Whether you're flying into Logan, keeping a car for the week, or need reliable wheels where the T can't take you, pick your community below to see local pickup details, the right cars, and pricing from $50/day.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/fleet" data-testid="locations-cta-fleet" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">Browse the fleet <ArrowRight size={16} /></Link>
            <Link to="/logan-airport-car-rental" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 font-semibold hover:border-blue-400 hover:text-blue-700 transition-colors"><Plane size={16} /> Logan Airport pickup</Link>
          </div>
        </div>
      </section>

      <section className="py-8 px-5 sm:px-8 max-w-6xl mx-auto space-y-12">
        {GROUPS.map((g) => (
          <div key={g.title} data-testid={`location-group-${g.title.split(" ")[0].toLowerCase()}`}>
            <h2 className="font-heading text-2xl font-bold text-slate-900">{g.title}</h2>
            <p className="text-slate-500 mt-1.5 max-w-2xl text-sm leading-relaxed">{g.blurb}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {g.cities.filter((c) => CITIES[c]).map((c) => (
                <Link key={c} to={`/${hubSlug(c)}`} data-testid={`location-link-${c}`} className="group bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-blue-300 hover:shadow-card transition-all">
                  <div className="flex items-center gap-2 text-slate-900 font-heading font-bold group-hover:text-blue-700 transition-colors"><MapPin size={16} className="text-blue-500" /> {CITIES[c].cityName}, MA</div>
                  <p className="text-slate-500 text-sm mt-1.5 line-clamp-2">{CITIES[c].heroBadge}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-blue-900 text-white px-8 py-12 text-center">
          <Plane size={26} className="mx-auto text-blue-300" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-3">Flying into Boston Logan?</h2>
          <p className="mt-3 text-blue-100 max-w-xl mx-auto">We offer Logan Airport (BOS) pickup &amp; drop-off with a professional, well-maintained fleet.</p>
          <Link to="/logan-airport-car-rental" className="inline-block mt-6 px-7 py-3 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors">Logan Airport car rental</Link>
        </div>
      </section>
    </div>
  );
}
